import { useChunkUpload } from '@core/shared';
import { BaseApiResponse, OptionsApi, WebinarAttachment, WebinarAttachmentType, WebinarAttachmentVisibility } from '@core/swagger-client';
import {
    Button,
    Close,
    CompositePageHeader,
    ContainerNoTopSpace,
    ContainerWebinarInner,
    ContentHeader,
    Dialog,
    Edit,
    ErrorMessage,
    ErrorMessagePage,
    Heading,
    Input,
    LoaderPage,
    PageHeader,
    PageHeaderContent,
    Select,
    Upload,
    useFileDropzone
} from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const WebinarAttachments = () => {
    const { t } = useTranslation();
    const [attachment, setAttachment] = useState<WebinarAttachment | null>(null);
    const [uploadResult, setUploadResult] = useState<BaseApiResponse | null>(null);

    const { webinarId } = useParams({ strict: false });

    const { upload, isUploading, progress } = useChunkUpload(`/api/webinar-attachments/${webinarId}/upload`);
    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: async (acceptedFiles) => {
            setUploadResult(null);
            for (const file of acceptedFiles) {
                const response = await upload(file);
                setUploadResult(response);
            }
            refreshAttachments();
        },
        accept: {
            'application/pdf': ['.pdf']
        }
    });

    const { data: webinar, error: webinarError, isLoading: webinarIsLoading } = useQuery(OptionsApi.webinar.get(webinarId!));
    const {
        data: attachments,
        error: attachmentsError,
        isLoading: attachmentsIsLoading,
        refetch: refreshAttachments
    } = useQuery(OptionsApi.webinarAttachments.get(webinarId!));

    const deleteAttachmentMutation = useMutation(OptionsApi.webinarAttachments.remove());
    const deleteAttachment = (attachment: WebinarAttachment) => {
        deleteAttachmentMutation.mutate(
            { webinarId: webinarId!, attachmentId: attachment.attachmentId },
            {
                onSuccess: () => {
                    refreshAttachments();
                }
            }
        );
    };

    const editAttachmentMutation = useMutation(OptionsApi.webinarAttachments.edit());
    const editAttachment = (attachment: WebinarAttachment) => {
        editAttachmentMutation.mutate(
            { webinarId: webinarId!, attachment },
            {
                onSuccess: () => {
                    refreshAttachments();
                    setAttachment(null);
                }
            }
        );
    };

    const error = webinarError || attachmentsError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (!webinar || !attachments || webinarIsLoading || attachmentsIsLoading) {
        return <LoaderPage></LoaderPage>;
    }

    return (
        <ContainerNoTopSpace>
            <PageHeader>
                <PageHeaderContent>
                    <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                </PageHeaderContent>
            </PageHeader>
            <ContainerWebinarInner>
                <BackToOriginLink fallback="content" />
                <ContentHeader contentHeader={t('Content.Attachments.PageHeading')} />
                {attachment && (
                    <Dialog open={true} onClose={() => setAttachment(null)} title={t('Content.Attachments.Dialog.Heading')}>
                        <Input
                            label={t('Content.Attachments.Dialog.Label.AttachmentTitle')}
                            value={attachment.title}
                            onChange={(e) => setAttachment({ ...attachment, title: e.target.value })}
                            required
                        ></Input>
                        <Select
                            label={t('Content.Attachments.Dialog.Label.AttachmentVisibility')}
                            value={attachment.visibility}
                            onChange={(e) => setAttachment({ ...attachment, visibility: parseInt(e.target.value) as WebinarAttachmentVisibility })}
                        >
                            {Object.keys(WebinarAttachmentVisibility)
                                .filter((key) => isNaN(Number(key))) // Only string keys
                                .map((key) => (
                                    <option key={key} value={WebinarAttachmentVisibility[key as keyof typeof WebinarAttachmentVisibility]}>
                                        {t(`Content.Attachments.Visibility.${key}`)}
                                    </option>
                                ))}
                        </Select>
                        {editAttachmentMutation.error && <ErrorMessage apiError={editAttachmentMutation.error} classNames="mt-4" />}
                        <div className="flex flex-wrap gap-4">
                            <Button
                                onClick={() => setAttachment(null)}
                                variant="primary-outline"
                                isLoading={deleteAttachmentMutation.isPending || editAttachmentMutation.isPending}
                            >
                                {t('Generic.Button.Cancel')}
                            </Button>
                            <Button
                                onClick={() => editAttachment(attachment)}
                                variant="primary"
                                isLoading={deleteAttachmentMutation.isPending || editAttachmentMutation.isPending}
                            >
                                {t('Generic.Button.SaveChanges')}
                            </Button>
                        </div>
                    </Dialog>
                )}

                <div className="relative max-w-4xl min-h-64 flex mb-8 lg:mb-16">
                    {!isUploading && (
                        <div
                            {...getRootProps()}
                            className={`group w-full ${isDragActive ? 'bg-accent-lighter' : 'bg-white'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-dashed border-2 ${isDragActive ? 'border-accent' : 'border-gray-400'} ${(rejectionCode || (uploadResult && !uploadResult.success)) && !isDragActive && 'border-warning'} hover:border-accent focus-visible:border-accent rounded-lg flex flex-col items-center justify-center p-6 outline-hidden transition-color duration-150 ease-in-out`}
                        >
                            <input data-testid="drop-input" {...getInputProps()} />
                            <div data-testid="dragAndDrop" className="flex flex-col items-center ">
                                <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                                <p className="font-accent font-w-heading text-3xl mb-2">
                                    {isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}
                                </p>
                                <p>
                                    <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                                </p>
                                <p className="mt-4">
                                    {t('Generic.DragDrop.AcceptedText')}
                                    <span className="font-medium">{t('Content.Attachments.DragDrop.AcceptedFileTypes')}</span>
                                </p>
                            </div>
                            {rejectionCode ? (
                                <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />
                            ) : (
                                uploadResult && !uploadResult.success && <ErrorMessage apiError={uploadResult} classNames="mt-4" />
                            )}
                        </div>
                    )}
                    {isUploading && (
                        <div className="relative flex-1 flex flex-col items-center justify-center bg-white border-dashed border-2 border-gray-400 rounded-lg p-6">
                            <label htmlFor="progress-bar" className="mb-2">
                                {t('Content.Attachments.Progress.Label')}
                            </label>
                            <progress id="progress-bar" className="progress" value={progress} max="100">
                                {progress}%
                            </progress>
                        </div>
                    )}
                </div>
                {attachments.length > 0 && (
                    <>
                        <Heading variant="h2" styleAs="h3" classNames="mb-4">
                            {attachments.length === 1 ? (
                                t('Content.Attachments.Heading.OneAttachment')
                            ) : (
                                <Trans i18nKey="Content.Attachments.Heading.MultipleAttachments" values={{ amount: attachments.length }} />
                            )}
                        </Heading>
                        <div className="table-responsive bg-white rounded-lg mt-4 shadow-md">
                            <table className="table table-striped mt-2">
                                <thead>
                                    <tr>
                                        <th>{t('Content.Attachments.Table.Header.Title')}</th>
                                        <th>{t('Content.Attachments.Table.Header.Visibility')}</th>
                                        <th>{t('Content.Attachments.Table.Header.Type')}</th>
                                        <th className="w-30"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attachments.map((attachment) => (
                                        <tr key={attachment.attachmentId}>
                                            <td>
                                                <a href={attachment.path} className="link" target="_blank" rel="noopener noreferrer">
                                                    {attachment.title}
                                                </a>
                                            </td>
                                            <td>{t(`Content.Attachments.Visibility.${WebinarAttachmentVisibility[attachment.visibility]}`)}</td>
                                            <td>{t(`Content.Attachments.Type.${WebinarAttachmentType[attachment.type]}`)}</td>
                                            <td>
                                                <Button
                                                    onClick={() => setAttachment(attachment)}
                                                    iconbtn
                                                    classNames="-my-4 translate-y-1.5"
                                                    isLoading={deleteAttachmentMutation.isPending || editAttachmentMutation.isPending}
                                                >
                                                    <Edit classNames="size-6 stroke-current" />
                                                    <span className="sr-only">
                                                        <Trans i18nKey="Content.Attachments.Button.Edit" values={{ file: attachment.title }} />
                                                    </span>
                                                </Button>
                                                <Button
                                                    onClick={() => deleteAttachment(attachment)}
                                                    iconbtn
                                                    classNames="-my-4 translate-y-1.5"
                                                    isLoading={deleteAttachmentMutation.isPending || editAttachmentMutation.isPending}
                                                >
                                                    <Close classNames="size-6 stroke-warning" />
                                                    <span className="sr-only">
                                                        <Trans i18nKey="Content.Attachments.Button.Delete" values={{ file: attachment.title }} />
                                                    </span>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
